import React, { useState, useEffect } from "react";
import {
  ThemeProvider,
  Button,
  Tooltip,
  Dialog,
  FormControlLabel,
  Paper,
  Switch,
} from "@mui/material";
import MaterialTable from "material-table";
import { getCertData, normalizeDate_notime } from "../../utilities";
import { tableIcons } from "../../components/Utilities/UtilitiesWD";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DangerousIcon from "@mui/icons-material/Dangerous";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { useNavigate } from "react-router-dom";
import { useAppMuiTheme } from "../../theme/MuiThemeBridge";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { LoadingState } from "../../components/UI/States";

// material-table sits flush inside an app-card: transparent container,
// tokenized header colors. Module scope so the Container isn't a new
// component every render.
const TableContainer = (props) => <Paper {...props} elevation={0} />;
const tableComponents = { Container: TableContainer };
const tableStyle = {
  backgroundColor: "transparent",
  color: "var(--app-text)",
  boxShadow: "none",
};
const headerStyle = {
  backgroundColor: "var(--app-surface-bg)",
  color: "var(--app-text-muted)",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.02em",
  whiteSpace: "nowrap",
  borderBottom: "1px solid var(--app-divider)",
};

const TaxCert = (props) => {
  // Local (nested) MUI theme: it replaces MuiThemeBridge for this subtree, so
  // it has to be the app theme or the table reverts to MUI defaults.
  const defaultMaterialTheme = useAppMuiTheme();
  const [certData, setCertData] = useState(null);
  const [image, setImage] = useState(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [showExpired, setExpired] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    getCertData(setCertData);
  }, []);

  function displayCertData() {
    if (showExpired) {
      return certData.filter((obj) => obj.Status === 1);
    } else {
      return certData;
    }
  }

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
      <PageShell size="2xl">
        <PageHeader
          icon={ReceiptLongRoundedIcon}
          tone="emerald"
          eyebrow="Everyday Tools"
          title="Customer Certificates"
          description="Tax-exemption certificates on file by customer and job. Open a certificate with the eye icon; expired ones are flagged."
          actions={
            <FormControlLabel
              control={
                <Switch
                  checked={showExpired}
                  onChange={() => setExpired(!showExpired)}
                />
              }
              label="Expired Only"
              sx={{ mr: 0 }}
            />
          }
        />
        {image && (
          <Dialog
            fullWidth={true}
            maxWidth="md"
            open={imageOpen}
            onClose={() => setImageOpen(false)}
          >
            <div className="flex w-full justify-center p-4">
              <img
                className="h-80 rounded-xl"
                src={"file://" + image}
                alt="Tax certificate"
              />
            </div>
          </Dialog>
        )}
        {certData ? (
          <ThemeProvider theme={defaultMaterialTheme}>
            <div className="app-card overflow-hidden rounded-2xl border">
              <MaterialTable
                style={tableStyle}
                components={tableComponents}
                columns={[
                  {
                    title: "State",
                    field: "State",
                  },
                  {
                    title: "Customer Code",
                    field: "Customer Code",
                  },
                  {
                    title: "Name",
                    field: "Name",
                  },
                  {
                    title: "Job Code",
                    field: "Job Code",
                  },
                  {
                    title: "Job Ref",
                    field: "Job Ref",
                  },
                  {
                    title: "Certificate Type",
                    field: "Certificate Type",
                  },
                  {
                    title: "Reason",
                    field: "Reason IN",
                    render: (rowData) => {
                      return (
                        <div className="flex">
                          {rowData["Reason IN"] && (
                            <p>{rowData["Reason IN"]}</p>
                          )}
                          {rowData["Reason MI"] && (
                            <p>{rowData["Reason MI"]}</p>
                          )}
                          {rowData["Reason OH"] && (
                            <p>{rowData["Reason OH"]}</p>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    title: "Form Received",
                    field: "Form Received",
                    render: (rowData) => {
                      return (
                        <p>{normalizeDate_notime(rowData["Form Received"])}</p>
                      );
                    },
                  },
                  {
                    title: "Expiration Date",
                    field: "Expiration Date",
                    render: (rowData) => {
                      if (rowData["Expiration Date"]) {
                        return (
                          <p>
                            {normalizeDate_notime(rowData["Expiration Date"])}
                          </p>
                        );
                      } else {
                        return <p>Never</p>;
                      }
                    },
                  },
                  {
                    title: "Notes",
                    field: "Notes",
                  },
                  {
                    title: "CERT",
                    field: "Cert",
                    render: (rowData) => {
                      return (
                        <div className="flex items-center gap-1">
                          {rowData["Cert"] && (
                            <Tooltip title="View Certificate">
                              <a
                                href={`/api/taxcert/image?file=${encodeURIComponent(
                                  rowData["Cert"]
                                )}`}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="View Certificate"
                                className="app-icon-btn h-9 w-9 text-sky-600 dark:text-sky-300"
                              >
                                <VisibilityIcon fontSize="small" />
                              </a>
                            </Tooltip>
                          )}
                          {rowData["Status"] == 1 && (
                            <Tooltip title="Certificate Expired">
                              <DangerousIcon
                                aria-label="Certificate Expired"
                                className="text-rose-600 dark:text-rose-400"
                              />
                            </Tooltip>
                          )}
                        </div>
                      );
                    },
                  },
                ]}
                data={displayCertData()}
                title={
                  <span className="app-muted text-sm font-medium tabular-nums">
                    {displayCertData().length.toLocaleString()}{" "}
                    {showExpired ? "expired " : ""}certificate
                    {displayCertData().length === 1 ? "" : "s"}
                  </span>
                }
                options={{
                  exportButton: true,
                  pageSize: 10, // make initial page size
                  emptyRowsWhenPaging: false, // To avoid of having empty rows
                  pageSizeOptions: [5, 10, 15, 25],
                  search: true,
                  sorting: true,
                  headerStyle: {
                    search: true,
                    ...headerStyle,
                  },
                  searchFieldStyle: {
                    color: "var(--app-text)",
                  },

                  rowStyle: (rowData) => {
                    return {
                      fontSize: 14,
                    };
                  },
                  sortIcon: "show",
                }}
                icons={tableIcons}
                localization={{
                  body: {
                    emptyDataSourceMessage: "No certificates were found",
                  },
                }}
              />
            </div>
          </ThemeProvider>
        ) : (
          <LoadingState label="Loading certificates…" />
        )}
      </PageShell>
    </div>
  );
};

export default TaxCert;
